import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BrutalButton } from "@/components/Brutal";
import { InfoTip } from "@/components/InfoTip";
import { Trophy, Sparkles, Loader2 } from "lucide-react";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import api from "@/lib/api";

const eur = (x) => "€" + (Number(x || 0)).toFixed(2).replace(".", ",");

const MODES = [
  { key: "prorata", label: "Naar rato" },
  { key: "fair_days", label: "Billijk (dagen weg)" },
  { key: "coulance", label: "Coulance (inhaalklus)" },
  { key: "manual", label: "Handmatig" },
];

export default function AllowanceWizard({ open, onClose, weekTitle }) {
  const t = useT();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [week, setWeek] = useState("previous");
  const [prop, setProp] = useState(null);
  const [dec, setDec] = useState({});

  const loadProposal = async (wk) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/rewards/allowance/proposal?week=${wk}`);
      setProp(data);
      const init = {};
      (data.members || []).forEach((m) => { init[m.user_id] = { mode: m.did_catchup ? "coulance" : "prorata", days_away: "", manual_amount: m.base_amount }; });
      setDec(init);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Zakgeld is niet actief"));
      setProp(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) loadProposal(week); /* eslint-disable-next-line */ }, [open, week]);

  if (!open) return null;
  const thr = prop?.threshold || 0;
  const amount = prop?.amount || 0;
  const title = prop?.week_title || weekTitle || "Zakgeld";

  const calc = (m) => {
    const d = dec[m.user_id] || {};
    const mode = d.mode || "prorata";
    if (mode === "manual") return Math.max(0, parseFloat(d.manual_amount || 0) || 0);
    if (thr <= 0) return 0;
    if (mode === "coulance") return m.did_catchup ? amount : amount * Math.min(1, m.points / thr);
    if (mode === "fair_days") {
      const dd = Math.max(0, Math.min(6, parseInt(d.days_away || 0) || 0));
      const eff = Math.max(1, thr * (7 - dd) / 7);
      return amount * Math.min(1, m.points / eff);
    }
    return amount * Math.min(1, m.points / thr);
  };
  const total = (prop?.members || []).reduce((s, m) => s + calc(m), 0);
  const upd = (uid, patch) => setDec((p) => ({ ...p, [uid]: { ...p[uid], ...patch } }));

  const finalize = async () => {
    setSending(true);
    try {
      const decisions = (prop.members || []).map((m) => ({ user_id: m.user_id, ...dec[m.user_id] }));
      const { data } = await api.post("/rewards/allowance/finalize", { week, decisions });
      toast.success(`${title} ${t("uitgekeerd")} · ${eur(data.total)} — ${t("iedereen is gemaild")} ✉️`);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || t("Uitkeren mislukt")); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !sending && onClose()}>
      <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="allowance-wizard">
        <DialogHeader><DialogTitle className="font-head font-black text-xl flex items-center gap-2"><Trophy className="w-5 h-5 text-brand-violet" strokeWidth={2.5} />{title} {t("afronden")}</DialogTitle></DialogHeader>

        <div className="flex gap-2">
          <button data-testid="allowance-week-previous" onClick={() => setWeek("previous")}
            className={`flex-1 brutal-sm brutal-press rounded-xl py-2 text-sm font-bold ${week === "previous" ? "bg-brand-ink text-white" : "bg-white"}`}>{t("Afgelopen week")}</button>
          <button data-testid="allowance-week-current" onClick={() => setWeek("current")}
            className={`flex-1 brutal-sm brutal-press rounded-xl py-2 text-sm font-bold ${week === "current" ? "bg-brand-ink text-white" : "bg-white"}`}>{t("Deze week")}</button>
        </div>

        {loading ? (
          <p className="py-8 text-center text-brand-ink/50"><Loader2 className="w-6 h-6 animate-spin inline" strokeWidth={2.5} /></p>
        ) : !prop ? (
          <p className="py-8 text-center text-brand-ink/50">{t("Geen gegevens")}</p>
        ) : (prop.members || []).length === 0 ? (
          <p className="py-8 text-center text-brand-ink/50">{t("Geen deelnemers voor het puntensysteem")}</p>
        ) : (
          <>
            <div className="brutal-sm rounded-xl bg-brand-cream p-3 text-sm font-semibold">
              {prop.week_start} t/m {prop.week_end} · {t("bedrag")} {eur(amount)} {t("bij")} {thr} {t("punten")}
            </div>
            <div className="space-y-2">
              {prop.members.map((m) => {
                const d = dec[m.user_id] || {};
                const mode = d.mode || "prorata";
                return (
                  <div key={m.user_id} data-testid={`allowance-member-${m.user_id}`} className="brutal-sm rounded-xl bg-white p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-head font-bold leading-tight truncate">{m.name}</p>
                        <p className="text-xs text-brand-ink/50 font-semibold">{m.points}/{thr} {t("pt")} · {m.pct}%{m.did_catchup ? " · 🔁 " + t("inhaalklus") : ""}</p>
                        {typeof m.achievable === "number" && (
                          <p data-testid={`allowance-achievable-${m.user_id}`} className="text-xs font-bold text-brand-ink/60 mt-0.5">
                            🎯 {t("haalbaar deze week")}: {m.achievable} {t("pt")} · {t("behaald")} {m.achieved_pct}%{m.achievable > 0 && m.achieved_pct >= 100 ? ` — ${t("alles gedaan wat kon 👏")}` : ""}
                          </p>
                        )}
                        {typeof m.dashboard_points === "number" && (
                          <p data-testid={`allowance-check-${m.user_id}`} className={`text-xs font-bold mt-0.5 ${m.dashboard_points === m.points ? "text-brand-green" : "text-red-500"}`}>
                            {m.dashboard_points === m.points
                              ? `✓ ${t("Dashboard klopt")} (${m.dashboard_points} ${t("pt")})`
                              : `⚠️ ${t("Dashboard")}: ${m.dashboard_points} · ${t("uitkering")}: ${m.points} ${t("pt")}`}
                          </p>
                        )}
                      </div>
                      <span data-testid={`allowance-amount-${m.user_id}`} className="font-head font-black text-brand-violet shrink-0">{eur(calc(m))}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 items-center">
                      {MODES.map((mo) => (
                        <button key={mo.key} data-testid={`allowance-mode-${m.user_id}-${mo.key}`} onClick={() => upd(m.user_id, { mode: mo.key })}
                          className={`brutal-sm brutal-press rounded-full px-2.5 py-1 text-xs font-bold ${mode === mo.key ? "bg-brand-violet text-white" : "bg-brand-cream"}`}>{t(mo.label)}</button>
                      ))}
                      <InfoTip testid={`allowance-mode-info-${m.user_id}`} text={
                        <span>
                          <b>Naar rato</b>: bedrag × (punten ÷ drempel). Zoveel je haalt, zoveel je krijgt.<br /><br />
                          <b>Billijk (dagen weg)</b>: de drempel telt naar rato van de aanwezige dagen. Vul het aantal dagen afwezig in; de drempel wordt dan drempel × (7 − dagen) ÷ 7, zodat afwezigheid niet oneerlijk telt.<br /><br />
                          <b>Coulance (inhaalklus)</b>: pakte deze persoon een <b>inhaalklus</b> uit de Klussen Pool op (🔁)? Dan het <b>volledige bedrag</b>, ongeacht de punten — een tweede kans voor wie achterloopt. Zonder inhaalklus telt het gewoon naar rato.<br /><br />
                          <b>Handmatig</b>: jij vult zelf een vast bedrag in.
                        </span>
                      } />
                    </div>
                    {mode === "fair_days" && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-xs font-bold text-brand-ink/60">{t("Dagen afwezig")}</span>
                        <input type="number" min="0" max="6" data-testid={`allowance-days-${m.user_id}`} value={d.days_away ?? ""} placeholder="0"
                          onChange={(e) => upd(m.user_id, { days_away: e.target.value === "" ? "" : Math.max(0, Math.min(6, parseInt(e.target.value, 10) || 0)) })}
                          className="w-16 brutal-sm rounded-lg px-2 py-1 text-sm font-bold text-center outline-none bg-brand-cream" />
                        <span className="text-xs text-brand-ink/40">{t("drempel telt naar rato aanwezige dagen")}</span>
                      </div>
                    )}
                    {mode === "manual" && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-sm font-bold">€</span>
                        <input type="number" min="0" step="0.01" data-testid={`allowance-manual-${m.user_id}`} value={d.manual_amount ?? ""} placeholder="0"
                          onChange={(e) => upd(m.user_id, { manual_amount: e.target.value === "" ? "" : Math.max(0, parseFloat(e.target.value) || 0) })}
                          className="w-24 brutal-sm rounded-lg px-2 py-1 text-sm font-bold text-center outline-none bg-brand-cream" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="brutal-sm rounded-xl bg-brand-yellow p-3 flex items-center justify-between">
              <span className="font-head font-bold flex items-center gap-1.5"><Sparkles className="w-4 h-4" strokeWidth={2.5} />{t("Totaal uit te keren")}</span>
              <span data-testid="allowance-total" className="font-head font-black text-lg">{eur(total)}</span>
            </div>
            <BrutalButton data-testid="allowance-finalize" onClick={finalize} disabled={sending} className="w-full">
              {sending ? t("Versturen…") : t("Versturen & uitbetalen")}
            </BrutalButton>
            <p className="text-xs text-brand-ink/40 text-center">{t("Iedereen krijgt zijn eigen bedrag per e-mail en melding. Jij ontvangt een overzicht.")}</p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
